import { useContext, useState, useRef, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

import { isNilOrEmpty, ifTrue } from 'src/tools/logic.tools';
import { t } from 'src/tools/text.tools';
import { resolvePathWithParams } from 'src/tools/url.tools';
import { logIncorrectData } from 'src/tools/log.tools';
import { useDebouncedFn } from 'src/hooks';

import { AutoCompleteDomains, AutoCompleteResult, ID, Result, ButtonBaseType } from 'src/models';
import { getParams } from 'src/store/app/app.reducer';
import { getTrades } from 'src/store/trades/trades.getters';
import { getCustomIsLoading } from 'src/store/loading/loading.reducer';
import { getCurrentSubprojectId } from 'src/store/app/app.getters';
import { cleanUpAfterTrades, requestTrades } from 'src/store/trades/trades.actions';
import { requestAutocomplete } from 'src/store/autocomplete/autocomplete.actions';
import { InputContext } from 'src/contexts/input.context';
import { PATHS } from 'src/data/routes.data';

import { P2 } from 'src/components/Typography';
import { IconBadge } from 'src/components/IconBadge';
import { InputWithIcons } from 'src/components/Inputs';
import { Dropdown } from 'src/components/Dropdown';
import { Menu } from 'src/components/Menu';

import classes from './TradeLinkSearchableDropdown.module.scss';

const TXT = t(['tradeLink']);

const domain = AutoCompleteDomains.TRADES_NAVBAR;

export function TradeLinkSearchableDropdown() {
  const { value, data, commonData, isReadonly, onDiscard, onChange } = useContext(InputContext);

  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);

  const [inputValue, setInputValue] = useState('');

  const [results, setResults] = useState<AutoCompleteResult[]>([]);

  const params = useSelector(getParams);

  const subProjectId = useSelector(getCurrentSubprojectId);

  const trades = useSelector(getTrades);

  const isLoading = useSelector(getCustomIsLoading)(domain);

  const defaultDropdownLabel = useMemo(() => {
    if (commonData)
      return {
        id: commonData.trade_contract_id,
        label: commonData.company ?? 'company unlinked',
        sub_label: commonData.trade,
      };
  }, [commonData]);

  const tradesList = useMemo(() => {
    if (trades)
      return trades
        .filter(t => t.id !== defaultDropdownLabel?.id)
        .map(t => {
          return {
            id: t.id,
            label: t.company_name,
            sub_label: t.name,
          };
        });
  }, [trades, defaultDropdownLabel]);

  const skipResponse = useRef(false);

  useEffect(() => {
    if (subProjectId) dispatch(requestTrades());

    return () => {
      dispatch(cleanUpAfterTrades());
    };
  }, [dispatch, subProjectId]);

  useEffect(() => {
    if (defaultDropdownLabel && tradesList) setResults([defaultDropdownLabel, ...tradesList]);
  }, [defaultDropdownLabel, tradesList]);

  useEffect(() => {
    if (!results.length && defaultDropdownLabel && tradesList) setResults([defaultDropdownLabel, ...tradesList]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  useEffect(() => {
    setIsOpen(!isReadonly);
  }, [isReadonly]);

  function handleResults(response: Result) {
    if (skipResponse.current || !response.isOK) {
      return;
    }

    setResults(response.payload as AutoCompleteResult[]);
  }

  const request = useDebouncedFn(sendRequest, 600);

  function sendRequest(query: string) {
    if (skipResponse.current) {
      return;
    }

    if (subProjectId) {
      dispatch(requestAutocomplete({ query, ids: [subProjectId], domain }, { callback: handleResults, id: domain }));
    } else {
      logIncorrectData('sendRequest', 'Missing subProjectId', { subProjectId });
    }
  }

  function handleOpenClose(value: boolean) {
    setResults([]);
    skipResponse.current = !value;
    setIsOpen(value);
    onDiscard?.();

    if (!value) {
      setInputValue('');
    }
  }

  function handleInputOnChange(value: string) {
    setInputValue(value);

    if (value.length > 1) {
      request(value);
      skipResponse.current = false;
    } else if (!value) {
      setResults([]);
      skipResponse.current = true;
    }
  }

  function handleItemOnClick(id: ID) {
    handleOpenClose(false);

    if (onChange) {
      onChange(id, true);
    }
  }

  function renderDropdownLabel({ label, sub_label }: ButtonBaseType) {
    return (
      <P2 title={`${sub_label} | ${label ?? 'company unlinked'}`}>
        <em>{sub_label}</em>
        <span className={ifTrue(!label, classes['unlinked-label'])}>{label ?? 'company unlinked'}</span>
      </P2>
    );
  }

  function renderBaseComponent() {
    if (isNilOrEmpty(data) || !value) return <></>;

    return (
      <div className='row w-100p a-center j-between'>
        <Link
          title={TXT('clickHereTradeAdmin')}
          to={resolvePathWithParams(PATHS.TRADE_SHOW, { ...params, tradeId: value })}
          rel='noopener noreferrer'
          className={classes.link}
        >
          <P2 className='link-label'>{data}</P2>
        </Link>
        <Link
          title={TXT('clickHereTradeDetail')}
          to={resolvePathWithParams(PATHS.REQ_DETAIL_SHOW, { ...params, tradeId: value })}
          rel='noopener noreferrer'
          className={classes['link-badge']}
        >
          <IconBadge iconId='dollar-fill' />
        </Link>
      </div>
    );
  }

  return (
    <Dropdown
      classNames={{
        container: clsx(classes.container),
        dropdown: ifTrue(!results.length, classes['border-none']),
      }}
      renderBaseComponent={renderBaseComponent}
      onRequestOpenClose={handleOpenClose}
      isOpen={isOpen}
      dropdownMinWidth='container-width'
    >
      <InputWithIcons
        className={classes.input}
        value={inputValue}
        onChange={handleInputOnChange}
        isLoading={isLoading}
        autoFocus
      />
      <Menu
        className={classes.menu}
        items={results}
        onItemClick={handleItemOnClick}
        renderItemLabel={renderDropdownLabel}
      />
    </Dropdown>
  );
}
